import {useState, useMemo, useCallback} from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Modal from '../../components/common/Modal/ModalComponent'
import {
  StyledSubtitle,
  StyledConfirmationButton,
  StyledConfirmationCancelButton,
  StyledInfoIcon
} from './AddClientModalStyle'
import Button from '@mui/material/Button'
import {useCreateClient} from '../api/createClient'
import {useCreateProjectDetails} from '../api/createProject'
import {useAddDeliveryAdmin} from '../api/addDeliveryAdmin'
import SnackbarComponent from '../common/Snackbar/SnackbarComponent'
import SnackbarMessages from '../common/Snackbar/SnackbarMessages.json'
import {StyledCancelButton, StyledTextField} from '../AdminPanelStyling'
import {validateClientName, validateField} from '../common/Helpers/formValidation'
import ValidationRules from './ValidationRules'
import TextFieldWithCounter from '../common/TextFieldWithCounter/TextFieldWithCounter'

const AddClientModal = ({open, onClose, onSave, clients, clientNameMaxLength = 50}) => {
  const [clientName, setClientName] = useState('')
  const [firstname, setFirstname] = useState('')
  const [lastname, setLastname] = useState('')
  const [email, setEmail] = useState('')
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)
  const [saving, setSaving] = useState(false)
  const [snackbar, setSnackbar] = useState({show: false, message: '', isError: false})

  const [addClient] = useCreateClient()
  const [createGroup] = useCreateProjectDetails()
  const [createUser] = useAddDeliveryAdmin()

  const trimmedFirstname = firstname.trim()
  const trimmedLastname = lastname.trim()
  const trimmedEmail = email.trim()

  const clientNameValidationError = useMemo(() => {
    if (clientName.length === 0) return ''
    return validateClientName(clientName, clientNameMaxLength)
  }, [clientName, clientNameMaxLength])

  const isClientNameValid = clientName.trim().length > 0 && !clientNameValidationError

  const isFirstnameValid = !validateField('firstName', trimmedFirstname)
  const isLastnameValid = !validateField('lastName', trimmedLastname)
  const isEmailValid = !validateField('email', trimmedEmail)

  const isDuplicateClientName = useMemo(
    () => clients.some((client) => (client.CLIENT_NAME || '').trim() === clientName.trim()),
    [clients, clientName]
  )

  const isAdminDetailsFilled = trimmedFirstname.length > 0 && trimmedLastname.length > 0 && trimmedEmail.length > 0
  const isSaveEnabled =
    isClientNameValid &&
    !isDuplicateClientName &&
    isAdminDetailsFilled &&
    isFirstnameValid &&
    isLastnameValid &&
    isEmailValid
  const hasUnsavedChanges = clientName !== '' || firstname !== '' || lastname !== '' || email !== ''

  const clientNameFieldError = useMemo(() => {
    if (clientName.length === 0) return ''
    if (clientNameValidationError) return clientNameValidationError
    if (isDuplicateClientName) return 'This name is already in use'
    return ''
  }, [clientName, clientNameValidationError, isDuplicateClientName])

  const getAdminFieldError = useCallback((field, value) => {
    if (!value.trim()) return ''
    return validateField(field, value.trim())
  }, [])

  const handleClientNameChange = (e) => {
    setClientName(e.target.value)
  }

  const handleFirstNameChange = (e) => {
    setFirstname(e.target.value)
  }

  const handleLastNameChange = (e) => {
    setLastname(e.target.value)
  }

  const handleEmailChange = (e) => {
    setEmail(e.target.value)
  }

  const handleCancel = () => {
    if (!hasUnsavedChanges) {
      onClose()
      return
    }

    setShowConfirmationDialog(true)
  }

  const closeConfirmationDialog = () => {
    setShowConfirmationDialog(false)
  }

  const handleConfirmLeave = () => {
    setShowConfirmationDialog(false)
    onClose()
  }

  const handleSaveClient = async () => {
    if (!isSaveEnabled || saving) return
    setSaving(true)
    try {
      const clientResult = await addClient({variables: {clientName: clientName.trim()}})
      if (!clientResult.data?.createClient?.createClient?.clientId) {
        throw new Error(SnackbarMessages.createClientError)
      }
      const clientId = clientResult.data.createClient.createClient.clientId.toString()

      const groupResult = await createGroup({
        variables: {
          clientId,
          groupName: 'Delivery Admin Group',
          groupType: '2'
        }
      })
      if (!groupResult.data?.creategroup?.userGroup?.group_id) {
        throw new Error(SnackbarMessages.createGroupError[0])
      }
      const groupId = groupResult.data.creategroup.userGroup.group_id.toString()

      const userResult = await createUser({
        variables: {
          groupId,
          email: trimmedEmail,
          firstName: trimmedFirstname,
          lastName: trimmedLastname
        }
      })

      if (userResult.errors?.length > 0) {
        setSnackbar({show: true, message: SnackbarMessages.createUserError, isError: true})
        return
      }

      setSnackbar({show: true, message: SnackbarMessages.clientAdded, isError: false})
      onSave?.()
      onClose()
    } catch {
      setSnackbar({show: true, message: SnackbarMessages.createClientError, isError: true})
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Modal
        open={open}
        onClose={handleCancel}
        title='Add New Client'
        aria={{labelledBy: 'add-client-modal'}}
        maxWidth='md'
        disableBackdropClick={true}
      >
        <Box display='flex' flexDirection='column' gap={3}>
          <Box display='flex' flexDirection='column' gap={1}>
            <TextFieldWithCounter
              value={clientName}
              onChange={handleClientNameChange}
              label='Client Name'
              maxLength={clientNameMaxLength}
              enforceMaxLength={false}
              fullWidth
              size='medium'
              error={!!clientNameFieldError}
              helperText={clientNameFieldError}
            />
            <ValidationRules refuseNumbers refuseSpecialCharacters />
          </Box>
          <Divider />
          <StyledSubtitle>Add Admin to this Client</StyledSubtitle>
          <Box display='flex' gap={1} alignItems='flex-start'>
            <StyledTextField
              value={firstname}
              onChange={handleFirstNameChange}
              label='First Name'
              fullWidth
              size='medium'
              error={!!getAdminFieldError('firstName', firstname)}
              helperText={getAdminFieldError('firstName', firstname)}
            />
            <StyledTextField
              value={lastname}
              onChange={handleLastNameChange}
              label='Last Name'
              fullWidth
              size='medium'
              error={!!getAdminFieldError('lastName', lastname)}
              helperText={getAdminFieldError('lastName', lastname)}
            />
            <StyledTextField
              value={email}
              onChange={handleEmailChange}
              label='Email'
              fullWidth
              size='medium'
              error={!!getAdminFieldError('email', email)}
              helperText={getAdminFieldError('email', email)}
            />
          </Box>
          <Box display='flex' justifyContent='flex-end' gap={1} mt={3}>
            <StyledCancelButton onClick={handleCancel}>Cancel</StyledCancelButton>
            <Button variant='contained' disabled={!isSaveEnabled || saving} onClick={handleSaveClient}>
              {saving ? (
                <Box display='flex' alignItems='center' gap={1}>
                  <CircularProgress size={20} color='inherit' />
                  Saving
                </Box>
              ) : (
                'Save Client'
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal
        open={showConfirmationDialog}
        onClose={closeConfirmationDialog}
        aria={{labelledBy: 'add-client--cancel-confirmation-modal'}}
        maxWidth='xs'
      >
        <Box display='flex' flexDirection='column' alignItems='center' gap={2} p={2}>
          <StyledInfoIcon fontSize='40px' />
          <Typography variant='h6' fontWeight='bold' textAlign='center'>
            Are you sure you want to leave?
          </Typography>
          <Typography variant='body1' textAlign='center'>
            Leaving will cancel the creation of this Client and you&apos;ll lose any changes unless you click
            &ldquo;Save Client&rdquo;.
          </Typography>
          <Box display='flex' flexDirection='column' alignItems='center' gap={1} width='80%' mt={2}>
            <StyledConfirmationButton onClick={handleConfirmLeave} variant='contained'>
              Yes, I&apos;d like to leave
            </StyledConfirmationButton>
            <StyledConfirmationCancelButton onClick={closeConfirmationDialog}>Cancel</StyledConfirmationCancelButton>
          </Box>
        </Box>
      </Modal>
      <SnackbarComponent
        showNotification={snackbar.show}
        setShowNotification={(show) => setSnackbar((prev) => ({...prev, show}))}
        message={snackbar.message}
        error={snackbar.isError}
      />
    </>
  )
}

AddClientModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  clients: PropTypes.arrayOf(
    PropTypes.shape({
      CLIENT_NAME: PropTypes.string
    })
  ).isRequired,
  clientNameMaxLength: PropTypes.number
}

export default AddClientModal
